import React, { useState, useEffect, useRef } from 'react';
import Editor from '@monaco-editor/react';
import apiService from '../../../services/api.service';
import './PrintTemplates.css';

const PrintTemplates = () => {
  const [templateContent, setTemplateContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentView, setCurrentView] = useState('code'); // 'code' or 'preview'
  const monacoEditorRef = useRef(null);

  useEffect(() => {
    loadTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Check for unsaved changes
    if (originalContent && templateContent !== originalContent) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [templateContent, originalContent]);

  // Warn before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getVisitorBadgeTemplate();
      
      if (response.data.success && response.data.data) {
        const content = response.data.data.bodyOfTheTemplate;
        setTemplateContent(content);
        setOriginalContent(content);
        showSuccess('Template loaded from database');
      }
    } catch (err) {
      // If template not found (404), load default template
      if (err.response?.status === 404) {
        await loadDefaultTemplate();
      } else {
        setError('Failed to load template: ' + (err.response?.data?.error || err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadDefaultTemplate = async () => {
    try {
      const cacheBuster = new Date().getTime();
      const response = await fetch(`/templates/visitor-badge-default.html?cb=${cacheBuster}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const htmlContent = await response.text();
      setTemplateContent(htmlContent);
      setOriginalContent(htmlContent);
      showSuccess('Default template loaded');
    } catch (error) {
      setError('Failed to load default template: ' + error.message);
      console.error('Fetch Error:', error);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');
      
      await apiService.saveVisitorBadgeTemplate(templateContent);
      setOriginalContent(templateContent);
      showSuccess('Template saved successfully!');
    } catch (err) {
      setError('Failed to save template: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the custom template? This will revert to the default template.')) {
      return;
    }

    try {
      setDeleting(true);
      setError('');
      setSuccessMessage('');
      
      await apiService.deleteVisitorBadgeTemplate();
      showSuccess('Custom template deleted. Loading default...');
      
      // Reload default template
      setTimeout(() => {
        loadDefaultTemplate();
      }, 500);
    } catch (err) {
      setError('Failed to delete template: ' + (err.response?.data?.error || err.message));
    } finally {
      setDeleting(false);
    }
  };

  const handleResetToDefault = async () => {
    if (!window.confirm('Are you sure you want to reset to the default template? Any unsaved changes will be lost.')) {
      return;
    }

    await loadDefaultTemplate();
  };

  const handleFormatCode = async () => {
    try {
      // Dynamic import for html-beautify
      const beautify = (await import('html-beautify')).default;
      
      const formatted = beautify(templateContent, {
        indent_size: 2,
        indent_char: ' ',
        max_preserve_newlines: 2,
        preserve_newlines: true,
        keep_array_indentation: false,
        break_chained_methods: false,
        indent_scripts: 'normal',
        brace_style: 'collapse',
        space_before_conditional: true,
        unescape_strings: false,
        jslint_happy: false,
        end_with_newline: false,
        wrap_line_length: 0,
        indent_inner_html: true,
        comma_first: false,
        e4x: false,
        indent_empty_lines: false
      });
      
      setTemplateContent(formatted);
      showSuccess('Code formatted!');
    } catch (error) {
      setError('Failed to format code: ' + error.message);
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(templateContent).then(() => {
      showSuccess('Template copied to clipboard!');
    }).catch(err => {
      setError('Failed to copy template: ' + err.message);
    });
  };

  const handleDownload = () => {
    const blob = new Blob([templateContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'visitor-badge-template.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleEditorChange = (value) => {
    setTemplateContent(value || '');
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="template-editor-container">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading template...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="template-editor-container">
        <div className="page-header">
          <h1>Visitor Badge Print Template</h1>
          <p className="page-description">
            Edit the HTML template for visitor badge printing. Use placeholders: 
            <code>{'{{TokenNo}}'}</code>, <code>{'{{NameofTheVisitor}}'}</code>, 
            <code>{'{{PhotoOfTheVisitor}}'}</code>, <code>{'{{MobileNo}}'}</code>, 
            <code>{'{{QRCode}}'}</code>
          </p>
        </div>

        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠</span>
            {error}
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success">
            <span className="alert-icon">✓</span>
            {successMessage}
          </div>
        )}

        {hasUnsavedChanges && (
          <div className="alert alert-warning">
            <span className="alert-icon">!</span>
            You have unsaved changes
          </div>
        )}

        <div className="view-switcher">
          <button
            className={`view-btn ${currentView === 'code' ? 'active' : ''}`}
            onClick={() => setCurrentView('code')}
          >
            <i className="fas fa-code"></i> Code Editor
          </button>
          <button
            className={`view-btn ${currentView === 'preview' ? 'active' : ''}`}
            onClick={() => setCurrentView('preview')}
          >
            <i className="fas fa-eye"></i> Preview
          </button>
        </div>

        <div className="action-buttons">
          {currentView === 'code' && (
            <>
              <button className="btn btn-secondary" onClick={handleFormatCode}>
                <i className="fas fa-align-left"></i> Format Code
              </button>
              <button className="btn btn-secondary" onClick={handleCopyToClipboard}>
                <i className="fas fa-copy"></i> Copy HTML
              </button>
            </>
          )}
          
          <button className="btn btn-secondary" onClick={handleDownload}>
            <i className="fas fa-download"></i> Download
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={handleResetToDefault}
            disabled={loading}
          >
            <i className="fas fa-undo"></i> Reset to Default
          </button>
          <button 
            className="btn btn-danger" 
            onClick={handleDelete}
            disabled={deleting}
          >
            <i className="fas fa-trash"></i> {deleting ? 'Deleting...' : 'Delete Custom'}
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges}
          >
            <i className="fas fa-save"></i> {saving ? 'Saving...' : 'Save Template'}
          </button>
        </div>

        <div className="editor-container">
          {currentView === 'code' ? (
            <div className="code-editor">
              <Editor
                height="600px"
                defaultLanguage="html"
                value={templateContent}
                onChange={handleEditorChange}
                onMount={(editor) => {
                  monacoEditorRef.current = editor;
                }}
                theme="vs-dark"
                options={{
                  minimap: { enabled: true },
                  fontSize: 14,
                  wordWrap: 'on',
                  automaticLayout: true,
                  formatOnPaste: true,
                  formatOnType: true,
                }}
              />
            </div>
          ) : (
            <div className="preview-container">
              <iframe
                title="Template Preview"
                srcDoc={templateContent}
                className="preview-iframe"
              />
            </div>
          )}
        </div>

        <div className="template-info">
          <h3>Available Placeholders:</h3>
          <ul>
            <li><code>{'{{TokenNo}}'}</code> - Visitor token/registration number</li>
            <li><code>{'{{NameofTheVisitor}}'}</code> - Full name of the visitor</li>
            <li><code>{'{{PhotoOfTheVisitor}}'}</code> - Photo image tag (automatically generated)</li>
            <li><code>{'{{MobileNo}}'}</code> - Mobile phone number</li>
            <li><code>{'{{QRCode}}'}</code> - QR code image tag (automatically generated)</li>
          </ul>
          <p className="note">
            <strong>Note:</strong> The badge size is set to 3.5" × 5" for printing. 
            Make sure your design fits within these dimensions.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrintTemplates;
